/**
 * TikTok pixel (ads measurement), kept off the page's critical path.
 *
 * The queue stub from TikTok's snippet is set up at once, so `ttq.page()` and
 * `ttq.track()` (useAdsConversion) can be called any time. The pixel script
 * itself (events.js, a third-party download) is fetched only once the visitor
 * has accepted cookies, and then only after the page's `load` event when the
 * browser is idle. Without consent nothing is loaded and nothing leaves the
 * browser; declining later revokes consent and removes the pixel's cookies.
 *
 * Page views: one for the landing page, then one per client-side navigation,
 * since the SPA never reloads the page the snippet was written for.
 * Conversions are sent by the server (server/utils/tiktokEvents.ts); the
 * `ttclid` of an ad click is kept here, with consent, for those events.
 */

interface Ttq extends Array<unknown> {
  methods: string[]
  setAndDefer: (target: Record<string, unknown>, method: string) => void
  instance: (id: string) => unknown
  load: (id: string, options?: Record<string, unknown>) => void
  page: () => void
  track: (event: string, props?: Record<string, unknown>) => void
  grantConsent: () => void
  revokeConsent: () => void
  _i?: Record<string, unknown[] & { _u?: string }>
  _t?: Record<string, number>
  _o?: Record<string, unknown>
}

type TtqWindow = Window & { TiktokAnalyticsObject?: string, ttq?: Ttq }

const PIXEL_SRC = 'https://analytics.tiktok.com/i18n/pixel/events.js'

/** TikTok's snippet without its script insert: the queue and the `load` method. */
function installStub(w: TtqWindow): Ttq {
  w.TiktokAnalyticsObject = 'ttq'
  const ttq = (w.ttq = w.ttq || ([] as unknown as Ttq))
  ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent']
  ttq.setAndDefer = (target, method) => {
    target[method] = function (...args: unknown[]) {
      (target as unknown as unknown[]).push([method, ...args])
    }
  }
  for (const method of ttq.methods) ttq.setAndDefer(ttq as unknown as Record<string, unknown>, method)
  ttq.instance = (id) => {
    const instance = ttq._i?.[id] || []
    for (const method of ttq.methods) ttq.setAndDefer(instance as unknown as Record<string, unknown>, method)
    return instance
  }
  ttq.load = (id, options) => {
    ttq._i = ttq._i || {}
    ttq._i[id] = []
    ttq._i[id]._u = PIXEL_SRC
    ttq._t = ttq._t || {}
    ttq._t[id] = +new Date()
    ttq._o = ttq._o || {}
    ttq._o[id] = options || {}
    const script = document.createElement('script')
    script.async = true
    script.src = `${PIXEL_SRC}?sdkid=${id}&lib=ttq`
    document.head.appendChild(script)
  }
  return ttq
}

/** After `load`, when the browser has nothing more urgent to do. */
function whenIdleAfterLoad(run: () => void): void {
  const idle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4000 })
    else setTimeout(run, 1500)
  }
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
}

/** The pixel's first-party cookies, set on the site's root domain. */
function forgetTiktok(): void {
  const root = location.hostname.replace(/^www\./, '')
  const names = document.cookie.split(';')
    .map(c => c.split('=')[0]!.trim())
    .filter(name => name === '_ttp' || name === '_tt_enable_cookie' || name.startsWith('ttcsid'))
  for (const name of names) {
    for (const domain of ['', `; domain=${root}`, `; domain=.${root}`]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`
    }
  }
}

export default defineNuxtPlugin(() => {
  const pixelId = useRuntimeConfig().public.tiktokPixelId as string
  if (!pixelId) return

  const ttq = installStub(window as TtqWindow)
  let loaded = false

  const loadPixel = () => {
    if (loaded) return
    loaded = true
    whenIdleAfterLoad(() => ttq.load(pixelId))
  }

  let lastPath = useRoute().path
  ttq.page()
  useRouter().afterEach((to) => {
    if (to.path === lastPath) return
    lastPath = to.path
    ttq.page()
  })

  // An ad click seen before consent is kept in memory only until the visitor decides.
  const ttclid = new URLSearchParams(location.search).get('ttclid')

  if (hasCookieConsent()) {
    if (ttclid) rememberTiktokClickId(ttclid)
    loadPixel()
  }

  window.addEventListener(COOKIE_CONSENT_EVENT, (event) => {
    const granted = (event as CustomEvent<{ granted?: boolean }>).detail?.granted
    if (granted) {
      if (ttclid) rememberTiktokClickId(ttclid)
      if (loaded) ttq.grantConsent()
      loadPixel()
    } else {
      if (loaded) ttq.revokeConsent()
      forgetTiktok()
      forgetTiktokClickId()
    }
  })
})
